import React, { useCallback, useEffect, useRef, useState } from 'react';
import './adminPanel.css';
import { fetchJson } from './adminApi';
import { clearAdminSession } from './adminAuth';
import { duration, fmt, jobKindLabel } from './adminFormat';
import OverviewTab from './OverviewTab';
import ChartsTab from './ChartsTab';
import MapTab from './MapTab';
import ListingsTab from './ListingsTab';
import ListingDetailDialog from './ListingDetailDialog';
import JobsTab from './JobsTab';
import AnalyticsTab from './AnalyticsTab';
import DatabaseTab from './DatabaseTab';
import PerformanceTab from './PerformanceTab';
import MapPerfTab from './MapPerfTab';

// Admin dashboard, laid out like the scraper's explorer web UI: overview,
// charts, map and listings over the active listings, plus scrape jobs, site usage and a DB browser.

const TABS = [
    ['overview', 'Overview'],
    ['charts', 'Charts'],
    ['map', 'Map'],
    ['listings', 'Listings'],
    ['jobs', 'Jobs'],
    ['analytics', 'Analytics'],
    ['database', 'Database'],
    ['performance', 'Performance'],
    ['map-speed', 'Map speed'],
];
const TAB_STORAGE_KEY = 'admin.tab';
const JOB_POLL_RUNNING_MS = 3000;
const JOB_POLL_IDLE_MS = 15000;
const DATA_REFRESH_RUNNING_MS = 15000;

const readSavedTab = () => {
    try {
        const saved = window.localStorage.getItem(TAB_STORAGE_KEY);
        return TABS.some(([key]) => key === saved) ? saved : 'overview';
    } catch (error) {
        return 'overview';
    }
};

// Re-colour the map when the site's light/dark toggle changes the body class.
const useThemeKey = () => {
    const [themeKey, setThemeKey] = useState(() => (document.body.classList.contains('theme-dark') ? 'dark' : 'light'));
    useEffect(() => {
        const observer = new MutationObserver(() => {
            setThemeKey(document.body.classList.contains('theme-dark') ? 'dark' : 'light');
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);
    return themeKey;
};

const AdminPanelPage = () => {
    const [activeTab, setActiveTab] = useState(readSavedTab);
    const [overview, setOverview] = useState(null);
    const [charts, setCharts] = useState(null);
    const [jobs, setJobs] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [detailUrl, setDetailUrl] = useState(null);
    const [error, setError] = useState('');
    const jobRunningRef = useRef(false);
    const themeKey = useThemeKey();

    const showTab = useCallback((key) => {
        setActiveTab(key);
        try {
            window.localStorage.setItem(TAB_STORAGE_KEY, key);
        } catch (storageError) {
            // Not remembered: fine.
        }
    }, []);

    const refreshData = useCallback(async () => {
        const [overviewResult, chartsResult] = await Promise.allSettled([
            fetchJson('/admin/explorer/overview'),
            fetchJson('/admin/explorer/charts'),
        ]);
        if (overviewResult.status === 'fulfilled') setOverview(overviewResult.value);
        if (chartsResult.status === 'fulfilled') setCharts(chartsResult.value);
        const failed = [overviewResult, chartsResult].find((result) => result.status === 'rejected');
        setError(failed ? failed.reason?.message || 'Could not load the dashboard.' : '');
        setRefreshKey((key) => key + 1);
    }, []);

    const pollJobs = useCallback(async () => {
        try {
            const payload = await fetchJson('/admin/jobs');
            const wasRunning = jobRunningRef.current;
            jobRunningRef.current = Boolean(payload?.current);
            setJobs(payload);
            if (wasRunning && !jobRunningRef.current) refreshData();
        } catch (err) {
            setJobs((prev) => prev || { current: null, history: [], status: {}, error: err.message });
        }
    }, [refreshData]);

    useEffect(() => {
        refreshData();
        pollJobs();
    }, [refreshData, pollJobs]);

    const running = Boolean(jobs?.current);
    useEffect(() => {
        const timer = window.setInterval(pollJobs, running ? JOB_POLL_RUNNING_MS : JOB_POLL_IDLE_MS);
        return () => window.clearInterval(timer);
    }, [pollJobs, running]);

    // While a job writes listings, keep the numbers fresh.
    useEffect(() => {
        if (!running) return undefined;
        const timer = window.setInterval(refreshData, DATA_REFRESH_RUNNING_MS);
        return () => window.clearInterval(timer);
    }, [refreshData, running]);

    const handleLogout = () => {
        clearAdminSession();
        window.location.reload();
    };

    const summary = overview
        ? (overview.total
            ? `${fmt(overview.total)} active listings · ${fmt(overview.kommuner)} kommuner · newest ${overview.newest || '—'}`
            : 'The active listings table is empty')
        : 'Loading…';

    return (
        <div className="admin-explorer">
            <header className="ax-topbar">
                <div className="ax-brand">
                    <svg className="ax-logo" viewBox="0 0 16 16" aria-hidden="true">
                        <rect width="16" height="16" rx="3" />
                        <path d="M3 12V7l5-4 5 4v5H9V9H7v3z" />
                    </svg>
                    <div>
                        <h1>hempejl admin</h1>
                        <p className="ax-subtitle">{summary}</p>
                    </div>
                </div>
                <div className="ax-topbar-actions">
                    {running && (
                        <button type="button" className="ax-job-pill" onClick={() => showTab('jobs')}>
                            <span className="ax-dot" aria-hidden="true" />
                            {jobKindLabel(jobs.current)} running · {duration(jobs.current.elapsed_s)}
                        </button>
                    )}
                    <button type="button" className="ax-button ax-button--secondary" onClick={() => { refreshData(); pollJobs(); }}>
                        Refresh
                    </button>
                    <button type="button" className="ax-button ax-button--secondary" onClick={handleLogout}>Log out</button>
                </div>
            </header>

            <nav className="ax-tabs" role="tablist" aria-label="Admin sections">
                {TABS.map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === key}
                        onClick={() => showTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </nav>

            <main className="ax-main">
                {error && <p className="ax-alert">{error}</p>}
                {activeTab === 'overview' && <OverviewTab overview={overview} charts={charts} onGoto={showTab} />}
                {activeTab === 'charts' && <ChartsTab charts={charts} />}
                {activeTab === 'map' && <MapTab onOpenDetail={setDetailUrl} themeKey={themeKey} />}
                {activeTab === 'listings' && <ListingsTab onOpenDetail={setDetailUrl} refreshKey={refreshKey} />}
                {activeTab === 'jobs' && <JobsTab jobs={jobs} onRefreshJobs={pollJobs} />}
                {activeTab === 'analytics' && <AnalyticsTab />}
                {activeTab === 'database' && <DatabaseTab />}
                {activeTab === 'performance' && <PerformanceTab />}
                {activeTab === 'map-speed' && <MapPerfTab />}
            </main>

            {detailUrl && <ListingDetailDialog listingUrl={detailUrl} onClose={() => setDetailUrl(null)} />}
        </div>
    );
};

export default AdminPanelPage;
